import {
  type PricingPlan,
  PricingPlanTabType,
  type Product,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { isTariffEditAllowed } from 'Services/ProductManagement/Features/Tariff/utils'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import useTypedSelector from 'Store'
import { EXTENSION_TICKET_GROUP_CODE } from 'Util/constants'
import { calculateISODurationLengthInMinutes, createDurationMap } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  type Theme as MuiTheme,
  OutlinedInput,
  Skeleton,
  styled,
  Typography
} from '@mui/material'
import { DataGrid, type GridCellParams, type GridColDef, useGridApiRef } from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { t } from 'ttag'

export interface RowType {
  id: string
  title: string
  gridId?: number
  [key: string]: string | number | undefined
}

const TITLE_COL_WIDTH = 50
const ROW_HEIGHT = 40
const COL_WIDTH = 150
const DATA_GRID_BORDER_WIDTH = 1

const IsCellEditedClassName = 'IsEdited'
const IsCellUpdatedClassName = 'IsUpdated'

const getEnabledCellBackgroundColor = (theme: MuiTheme) =>
  theme.palette.mode === 'light' ? theme.palette.background.default : theme.palette.background.paper
const getHeaderCellBackgroundColor = (theme: MuiTheme) =>
  theme.palette.mode === 'light' ? theme.palette.background.paper : theme.palette.background.default

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  borderWidth: DATA_GRID_BORDER_WIDTH,
  borderColor: theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[900],
  WebkitFontSmoothing: 'auto',
  letterSpacing: 'normal',
  '& .MuiDataGrid-iconSeparator': {
    display: 'none'
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: getHeaderCellBackgroundColor(theme),
    '&:last-child': {
      borderRightWidth: 0
    }
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold'
  },
  '& .MuiDataGrid-cell': {
    backgroundColor: getEnabledCellBackgroundColor(theme),
    '&--disabled': {
      backgroundColor: theme.palette.action.disabledBackground
    },
    '&--editing': {
      backgroundColor: getEnabledCellBackgroundColor(theme) + ' !important'
    }
  },
  '& .MuiPaginationItem-root': {
    borderRadius: 0
  },
  '.MuiDataGrid-row': {
    '& .MuiDataGrid-cell': {
      // Account for the first child which is MuiDataGrid-cellOffsetLeft
      '&:nth-of-type(2)': {
        backgroundColor: getHeaderCellBackgroundColor(theme),
        fontWeight: 'bold'
      },
      '&:last-child': {
        borderRightWidth: 0
      },
      '&.IsEdited': {
        backgroundColor: theme.palette.focus.main
      },
      '&.IsUpdated': {
        backgroundColor: theme.palette.successSelected.main
      }
    },
    '&:hover': {
      backgroundColor: 'inherit'
    },
    '&.MuiDataGrid-row--lastVisible': {
      '& .MuiDataGrid-cell': {
        borderBottomWidth: 0
      }
    }
  }
}))

function CustomNoRowsOverlay(rows: number) {
  return (
    <>
      <LinearProgress />
      {Array.from({ length: rows }, x => (
        <Skeleton key={`skeleton-row-${x}`} height={ROW_HEIGHT} animation="wave" />
      ))}
    </>
  )
}

/**
 * Generates a unique title for a pricing plan based on its properties. Pricings and title
 * are excluded from the generated title.
 */
const generatePricingPlanTitle = (pricingPlan: PricingPlan, productId: string) => {
  const {
    active_period,
    custom_fields,
    description,
    domicile_class,
    visible_period,
    usage_validity_duration,
    vat_code,
    zone_map
  } = pricingPlan

  return `${productId}-${JSON.stringify(active_period)}-${JSON.stringify(
    custom_fields
  )}-${description}-${domicile_class}-${JSON.stringify(
    visible_period
  )}-${usage_validity_duration}-${vat_code}-${zone_map.join('')}`
}

function ProductPricingGrid({
  pricingPlans,
  zoneDefinition,
  product,
  domicileClass,
  setIsEdited,
  handleOpenActivePeriod,
  setProductPricingPlans,
  pricingPlanActivePeriod,
  pricingGridType,
  editedPricingPlanCells,
  setEditedPricingPlanCells,
  updatedPricingPlanCells
}: Readonly<{
  pricingPlans: PricingPlan[]
  zoneDefinition: ZoneDefinition
  product: Product
  domicileClass: string
  setIsEdited: (isEdited: boolean) => void
  handleOpenActivePeriod: () => void
  setProductPricingPlans: React.Dispatch<React.SetStateAction<PricingPlan[]>>
  pricingPlanActivePeriod: Validity | null
  pricingGridType: PricingPlanTabType
  editedPricingPlanCells: RowType[]
  setEditedPricingPlanCells: React.Dispatch<React.SetStateAction<RowType[]>>
  updatedPricingPlanCells: RowType[]
}>) {
  const apiRef = useGridApiRef()
  const allZones = Array.from(new Set(zoneDefinition.zones.flat()))
  const [rows, setRows] = useState<RowType[]>([])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const copyPasteMenuOpen = Boolean(anchorEl)
  const { copiedPricingPlan, setCopiedPricingPlan } = useContext(TariffContext)
  const { singleTariff } = useTypedSelector(state => state.tariff)

  const [currentDomicileClassPricingPlans, setCurrentDomicileClassPricingPlans] = useState<
    PricingPlan[]
  >(
    pricingPlans.filter(
      pricingPlan =>
        pricingPlan.domicile_class === domicileClass &&
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )
  )
  /**
   * Filters the pricing plans for the current domicile class and which belong to special_zones.
   * Excludes pricing plans that are not part of special_zones.
   */
  const domicileClassSpecialZonePricingPlans = pricingPlans.filter(
    pricingPlan =>
      pricingPlan.domicile_class === domicileClass &&
      zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
  )
  const [
    currentDomicileClassSpecialZonePricingPlans,
    setCurrentDomicileClassSpecialZonePricingPlans
  ] = useState<PricingPlan[]>(domicileClassSpecialZonePricingPlans)

  // ROW_HEIGHT is multiplied by the number of zones + 1 to account for the title row.
  const dataGridContainerHeight = ROW_HEIGHT * (allZones.length + 1) + DATA_GRID_BORDER_WIDTH * 2

  const isExchangePeriodTab = pricingGridType === PricingPlanTabType.EXCHANGE_PERIOD

  const gridValueFormatter = (value: number | string | null) => {
    if (value == null || value === '') {
      return ''
    }

    if (typeof value === 'string') {
      return value
    }

    if (isExchangePeriodTab) {
      return new Intl.NumberFormat('fi-FI', {
        style: 'unit',
        unit: 'minute'
      }).format(value)
    } else {
      return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(value)
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: '',
      width: TITLE_COL_WIDTH,
      type: 'string',
      sortable: false,
      headerAlign: 'center',
      align: 'center'
    },
    ...allZones.map(zoneId => ({
      field: zoneId,
      headerName: getTranslation(zoneDefinition.translations[zoneId], 'fi').value,
      width: COL_WIDTH,
      type: 'number' as const,
      editable: true,
      sortable: false,
      headerAlign: 'center' as const,
      flex: 1,
      valueFormatter: gridValueFormatter,
      cellClassName: (params: GridCellParams) => {
        if (params.value === undefined) {
          return 'MuiDataGrid-cell--disabled'
        }
        return ''
      }
    }))
  ]

  // Create rows and empty cells for each zoneDefinition
  // Row id is the first zone in zoneMap since it is the starting zone
  const createRows = () => {
    const newRows: RowType[] = []

    // The zoneDefinition.zones consists of all the possible zone combinations (zoneMaps).
    // Each zoneMap is an array of zoneIds, such as ['20', '21', '22']
    for (const zoneMap of zoneDefinition.zones) {
      const currentZone = zoneMap[0]
      const existingRow = newRows.find(row => row.id === currentZone)
      const row: RowType = existingRow ?? { id: '', title: '' }
      row.id = currentZone
      row.title = columns.find(column => column.field === currentZone)?.headerName ?? ''
      const lastZone = zoneMap[zoneMap.length - 1]
      // Cells are empty by default. Exchange period cells are disabled by default.
      row[lastZone] = isExchangePeriodTab ? undefined : ''

      if (!existingRow) {
        newRows.push(row)
      }
    }

    return newRows
  }

  const addExistingPricings = (newRows: RowType[]) => {
    // Filter out the pricing plans that are not for the current domicile class and are not special zones
    const filteredDomicileClassPricingPlans = currentDomicileClassPricingPlans.filter(
      pricingPlan =>
        pricingPlan.domicile_class === domicileClass &&
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )
    // Add existing pricing plans to rows
    filteredDomicileClassPricingPlans.forEach(pricingPlan => {
      const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
      if (!row) return
      const targetZone = pricingPlan.zone_map[pricingPlan.zone_map.length - 1]
      row[targetZone] = pricingPlan.pricings[0].price
    })

    return newRows
  }

  const addExistingExchangePeriods = (newRows: RowType[]) => {
    // Filter out the pricing plans that are not for the current domicile class and are not special zones
    const filteredDomicileClassPricingPlans = currentDomicileClassPricingPlans.filter(
      pricingPlan =>
        pricingPlan.domicile_class === domicileClass &&
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )
    // Add existing pricing plans to rows
    filteredDomicileClassPricingPlans.forEach(pricingPlan => {
      const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
      if (!row) return
      const targetZone = pricingPlan.zone_map[pricingPlan.zone_map.length - 1]
      if (pricingPlan.usage_validity_duration) {
        row[targetZone] = calculateISODurationLengthInMinutes(pricingPlan.usage_validity_duration)
      } else {
        row[targetZone] = ''
      }
    })

    return newRows
  }

  const resetRows = () => {
    let newRows = createRows()
    if (isExchangePeriodTab) {
      newRows = addExistingExchangePeriods(newRows)
    } else {
      newRows = addExistingPricings(newRows)
    }
    setRows(newRows)
  }

  useEffect(() => {
    resetRows()
    setCurrentDomicileClassPricingPlans(
      pricingPlans.filter(
        pricingPlan =>
          pricingPlan.domicile_class === domicileClass &&
          !zoneDefinition.special_zones.some(
            zone => pricingPlan.zone_map.join('') === zone.join('')
          )
      )
    )
  }, [pricingPlans, zoneDefinition])

  /**
   * Updates an existing pricing plan with the latest price.
   * If no pricing plan exists, creates a new one.
   * If the price is empty and a pricing plan exists, removes the existing pricing plan.
   * @param {string[]} zoneMap - The zone map to update or create a pricing plan for.
   * @param {number | string | null | undefined} price - The latest price for the zone map.
   * @param {PricingPlan[]} currentPricingPlans - The array of current pricing plans.
   * @returns {PricingPlan[]} The updated array of pricing plans.
   */
  const updateOrCreatePricingPlan = (
    zoneMap: string[],
    price: number | string | null | undefined,
    currentPricingPlans: PricingPlan[]
  ): PricingPlan[] => {
    const existingPricingPlanIndex = findExistingPricingPlanIndex(currentPricingPlans, zoneMap)

    if (price === '' || price == null) {
      if (existingPricingPlanIndex > -1) {
        // Remove the existing pricing plan if the price is empty
        currentPricingPlans.splice(existingPricingPlanIndex, 1)
      }
      // If the price is empty, there is no need to create a new pricing plan
      return currentPricingPlans
    }

    if (existingPricingPlanIndex > -1) {
      const existingPricingPlanClone = {
        ...structuredClone(currentPricingPlans[existingPricingPlanIndex]),
        title: generatePricingPlanTitle(currentPricingPlans[existingPricingPlanIndex], product.id)
      }
      if (existingPricingPlanClone.pricings.length > 0) {
        existingPricingPlanClone.pricings[0].price = price as number
        existingPricingPlanClone.pricings[0].title = generatePricingPlanTitle(
          existingPricingPlanClone,
          product.id
        )
      } else {
        existingPricingPlanClone.pricings[0] = {
          price: price as number,
          currency: 'EUR',
          vat_included: true,
          title: generatePricingPlanTitle(existingPricingPlanClone, product.id),
          description: ''
        }
      }

      currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
    } else {
      const newPricingPlanData = {
        active_period: pricingPlanActivePeriod,
        visible_period: null,
        domicile_class: domicileClass,
        custom_fields: {},
        description: '',
        usage_validity_duration: null,
        zone_map: zoneMap
      } as PricingPlan
      currentPricingPlans.push({
        ...newPricingPlanData,
        pricings: [
          {
            price: price as number,
            currency: 'EUR',
            vat_included: true,
            title: generatePricingPlanTitle(newPricingPlanData, product.id),
            description: ''
          }
        ],
        title: generatePricingPlanTitle(newPricingPlanData, product.id)
      })
    }

    return currentPricingPlans
  }

  const updateOrCreatePricingExchangePeriod = (
    zoneMap: string[],
    validityLength: number | string | null | undefined,
    currentPricingPlans: PricingPlan[]
  ): PricingPlan[] => {
    const existingPricingPlanIndex = findExistingPricingPlanIndex(currentPricingPlans, zoneMap)

    if (validityLength === '' || validityLength == null) {
      if (existingPricingPlanIndex > -1) {
        // Don't remove the entire plan, just update the validity
        const existingPricingPlanClone = structuredClone(
          currentPricingPlans[existingPricingPlanIndex]
        )
        existingPricingPlanClone.usage_validity_duration = null
        currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
      }
      return currentPricingPlans
    }

    if (existingPricingPlanIndex > -1) {
      const existingPricingPlanClone = structuredClone(
        currentPricingPlans[existingPricingPlanIndex]
      )
      existingPricingPlanClone.usage_validity_duration = createDurationMap(
        validityLength as number
      ).minutes
      currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
    }

    return currentPricingPlans
  }

  const isExtensionTicket = product.product_group === EXTENSION_TICKET_GROUP_CODE

  const getPricingPlanTranslation = (zoneMap: string[]) => {
    return zoneMap
      .map(zoneId => getTranslation(zoneDefinition.translations[zoneId], 'fi').value)
      .join('+')
  }

  /**
   * Gets the price of the special zone pricing plan for the given zone map.
   *
   * @param zoneMap
   * @param specialZonePricingPlans
   * @returns {price | ''} The price of the special zone pricing plan for the given zone map, or an empty string if not found.
   */
  const getSpecialZonePricingPlanPrice = (
    zoneMap: string[],
    specialZonePricingPlans: PricingPlan[]
  ) => {
    const specialZonePricingPlan = specialZonePricingPlans.find(
      pricingPlan =>
        pricingPlan.active_period?.title === pricingPlanActivePeriod?.title &&
        pricingPlan.domicile_class === domicileClass &&
        pricingPlan.zone_map.join('') === zoneMap.join('')
    )

    if (isExchangePeriodTab) {
      return specialZonePricingPlan?.usage_validity_duration
        ? calculateISODurationLengthInMinutes(specialZonePricingPlan.usage_validity_duration)
        : ''
    } else {
      return specialZonePricingPlan?.pricings[0].price ?? ''
    }
  }

  const filterSpecialZonePricingPlans = (
    zoneDefinition: ZoneDefinition,
    pricingPlan: PricingPlan
  ) => {
    return !zoneDefinition.special_zones.some(
      zone => pricingPlan.zone_map.join('') === zone.join('')
    )
  }

  /**
   * Handles changing the price of a special zone pricing plan or creating a new one if it does not exist.
   *
   * @param zoneMap
   * @param price
   * @param specialZonePricingPlans
   */
  const handleSpecialZonePricingPlanPriceChange = (
    zoneMap: string[],
    price: string,
    specialZonePricingPlans: PricingPlan[]
  ) => {
    let currentSpecialZonePricingPlans: PricingPlan[] = [...specialZonePricingPlans]

    if (isExchangePeriodTab) {
      // Update or create pricing plan for exchange period
      currentSpecialZonePricingPlans = updateOrCreatePricingExchangePeriod(
        zoneMap,
        price,
        currentSpecialZonePricingPlans
      )
    } else {
      currentSpecialZonePricingPlans = updateOrCreatePricingPlan(
        zoneMap,
        price,
        currentSpecialZonePricingPlans
      )
    }

    setProductPricingPlans(prevState => {
      let otherDomicilePricingPlans: PricingPlan[] = []
      // Filter out the pricing plans that are not in special zones
      if (pricingPlanActivePeriod === null) {
        otherDomicilePricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period !== pricingPlanActivePeriod ||
            pricingPlan.domicile_class !== domicileClass ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      } else {
        otherDomicilePricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period?.title !== pricingPlanActivePeriod.title ||
            pricingPlan.domicile_class !== domicileClass ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      }

      setIsEdited(true)
      // Combine the updated special zone pricing plans with the other domicile class pricing plans
      return [...otherDomicilePricingPlans, ...currentSpecialZonePricingPlans]
    })
  }

  /**
   * Finds the index of an existing pricing plan for the given zone map.
   * @param pricingPlans
   * @param {Array} zoneMap - Array of zoneIds.
   * @returns {Number} Index of existing pricing plan, or -1 if not found.
   */
  const findExistingPricingPlanIndex = (pricingPlans: PricingPlan[], zoneMap: string[]): number => {
    return pricingPlans.findIndex(
      pricingPlan =>
        zoneMap.length === pricingPlan.zone_map.length &&
        zoneMap.every((zone, index) => zone === pricingPlan.zone_map[index])
    )
  }

  const openCopyPasteMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeCopyPasteMenu = () => {
    setAnchorEl(null)
  }

  const copyPricingPlan = () => {
    setCopiedPricingPlan([
      ...currentDomicileClassPricingPlans,
      ...currentDomicileClassSpecialZonePricingPlans
    ])
    SnackbarUtils.info(t`Pricing plan copied successfully`)
    setAnchorEl(null)
  }

  const pastePricingPlan = () => {
    if (copiedPricingPlan) {
      // We want to add the same domicileclass to the pasted pricing plan as the selected domicile
      // so it maps correctly to the grid when saving the pasted pricing plan
      const pricingPlansWithCorrectDomicileClass = copiedPricingPlan.map(plan => ({
        ...plan,
        domicile_class: domicileClass
      }))

      // Filter out the pricing plans that are not in special zones.
      // Special zones are only copied for extension tickets.
      const pricingPlansWithoutSpecialZones = pricingPlansWithCorrectDomicileClass.filter(
        pricingPlan =>
          !zoneDefinition.special_zones.some(
            zone => pricingPlan.zone_map.join('') === zone.join('')
          )
      )

      setCurrentDomicileClassPricingPlans(pricingPlansWithoutSpecialZones)
      // Replace only the pricing plans which have same active period and same domicile class
      // as the copied pricing plans
      setProductPricingPlans(prevState => [
        ...prevState.filter(
          plan =>
            plan.active_period?.title !== pricingPlanActivePeriod?.title ||
            plan.domicile_class !== domicileClass
        ),
        ...pricingPlansWithoutSpecialZones.filter(
          pricingPlan =>
            pricingPlan.active_period === pricingPlanActivePeriod &&
            pricingPlan.domicile_class === domicileClass
        )
      ])

      if (isExtensionTicket) {
        // Filter out the pricing plans that are in special zones.
        // When copying a pricing plan for an extension ticket,
        // we also want to copy the special zones pricing plans
        const pricingPlansWithSpecialZones = pricingPlansWithCorrectDomicileClass.filter(
          pricingPlan =>
            zoneDefinition.special_zones.some(
              zone => pricingPlan.zone_map.join('') === zone.join('')
            )
        )

        setCurrentDomicileClassSpecialZonePricingPlans(pricingPlansWithSpecialZones)
        setProductPricingPlans(prevState => [...prevState, ...pricingPlansWithSpecialZones])
      }
      setIsEdited(true)
    }
    setAnchorEl(null)
  }

  /**
   * Handles the update of a pricing plan cell.
   * This function updates the pricing plans based on the modified row.
   * It finds all zone maps that start with the modified row's ID and updates each relevant zone map
   * with the new value. If the updated pricing plans are the same as the original pricing plans,
   * it does not update the parent component.
   * Otherwise, it updates the parent component with the new pricing plans.
   */
  const handlePricingPlanCellUpdate = (newRow: RowType, newRows: RowType[]) => {
    // Update pricing plans based on the modified row
    let currentPricingPlans: PricingPlan[] = [...currentDomicileClassPricingPlans]

    // Find all zone maps that start with this row's ID
    const relevantZoneMaps = zoneDefinition.zones.filter(zoneMap => zoneMap[0] === newRow.id)

    // Update each relevant zone map with the new value
    for (const zoneMap of relevantZoneMaps) {
      const lastZone = zoneMap[zoneMap.length - 1]
      const lastZonePriceValue = newRow[lastZone]

      if (isExchangePeriodTab) {
        // Update or create pricing plan for exchange period
        currentPricingPlans = updateOrCreatePricingExchangePeriod(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlans
        )
      } else {
        currentPricingPlans = updateOrCreatePricingPlan(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlans
        )
      }
    }

    // Set the updated pricing plans
    setCurrentDomicileClassPricingPlans(currentPricingPlans)

    const updatedPricingPlans = [
      ...currentPricingPlans,
      ...currentDomicileClassSpecialZonePricingPlans
    ]

    // Check if the updated pricing plans are the same as the original pricing plans
    // If they are the same, we do not need to update the parent component
    if (JSON.stringify(updatedPricingPlans) === JSON.stringify(pricingPlans)) {
      return newRows
    }

    // Update the parent component and append values to original state
    // Replace the pricing plans in the parent component which have
    // the same active period title and same domicile class
    setProductPricingPlans(prevState => {
      if (pricingPlanActivePeriod === null) {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period !== pricingPlanActivePeriod || plan.domicile_class !== domicileClass
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      } else {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period?.title !== pricingPlanActivePeriod.title ||
            plan.domicile_class !== domicileClass
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      }
    })
    setIsEdited(true)
  }

  const isSpecialZonePricingPlanDisabled = (zoneMap: string[]) => {
    if (isExchangePeriodTab) {
      const specialZonePricingPlan = pricingPlans.find(
        pricingPlan =>
          pricingPlan.domicile_class === domicileClass &&
          pricingPlan.zone_map.join('') === zoneMap.join('')
      )
      return specialZonePricingPlan === undefined || specialZonePricingPlan?.pricings.length === 0
    }
    return false
  }

  const isOriginalPricingPlan = pricingPlanActivePeriod == null

  const getCellClassName = (params: GridCellParams) => {
    if (editedPricingPlanCells.length > 0) {
      return editedPricingPlanCells.some(
        cell =>
          cell.id === params.id &&
          cell.field === params.field &&
          cell.gridId === apiRef.current.instanceId.id
      )
        ? IsCellEditedClassName
        : ''
    }

    return updatedPricingPlanCells.some(
      cell =>
        cell.id === params.id &&
        cell.field === params.field &&
        cell.gridId === apiRef.current.instanceId.id
    )
      ? IsCellUpdatedClassName
      : ''
  }

  const updateEditedPricingPlanCells = (newRow: RowType, oldRow: RowType) => {
    const newEditedCell = {
      id: newRow.id,
      // Find which field has been edited by comparing all properties between newRow and oldRow
      field: Object.keys(newRow).find(key => newRow[key] !== oldRow[key]) || '',
      title: newRow.title,
      gridId: apiRef.current.instanceId.id
    } as RowType

    if (
      newEditedCell.field &&
      !editedPricingPlanCells.some(
        existingCell =>
          existingCell.id === newEditedCell.id &&
          existingCell.field === newEditedCell.field &&
          existingCell.gridId === newEditedCell.gridId
      )
    ) {
      setEditedPricingPlanCells((prevState: RowType[]) => [...prevState, newEditedCell])
    }
  }

  return (
    <Box>
      <Box sx={{ height: dataGridContainerHeight }}>
        <StyledDataGrid
          apiRef={apiRef}
          hideFooter
          disableColumnFilter={true}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={ROW_HEIGHT}
          showCellVerticalBorder
          showColumnVerticalBorder
          processRowUpdate={(newRow, oldRow) => {
            setRows(prevRows => {
              const newRows = [...prevRows]
              const rowIndex = newRows.findIndex(row => row.id === newRow.id)
              newRows[rowIndex] = newRow as RowType

              handlePricingPlanCellUpdate(newRow as RowType, newRows)
              return newRows
            })

            updateEditedPricingPlanCells(newRow as RowType, oldRow as RowType)
            return newRow
          }}
          getCellClassName={getCellClassName}
          disableColumnMenu={true}
          isCellEditable={params => {
            // Disabled here, since DataGrid types do not support union types for cells
            return (
              params.value !== undefined &&
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              params.value !== '-' &&
              isTariffEditAllowed(singleTariff)
            )
          }}
          onCellClick={(params: GridCellParams) => {
            if (params.cellMode === 'edit') return
            apiRef.current.startCellEditMode({
              id: params.id,
              field: params.field
            })
          }}
          disableRowSelectionOnClick={true}
          columns={columns}
          rows={rows}
          slots={{
            noRowsOverlay: () => CustomNoRowsOverlay(allZones.length)
          }}
        />
      </Box>
      {isExtensionTicket && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ my: 2 }}
          >{t`Additional zones`}</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {zoneDefinition.special_zones.map((zoneMap, _index) => {
              return (
                <FormControl
                  sx={{ width: 129 }}
                  variant="outlined"
                  key={`${getPricingPlanTranslation(zoneMap)}-zone-price`}
                >
                  <InputLabel htmlFor={`${getPricingPlanTranslation(zoneMap)}-zone-price`}>
                    {getPricingPlanTranslation(zoneMap)}
                  </InputLabel>
                  <OutlinedInput
                    id={`${getPricingPlanTranslation(zoneMap)}-zone-price`}
                    type="number"
                    endAdornment={
                      <InputAdornment position="end">
                        {isExchangePeriodTab ? 'min' : '€'}
                      </InputAdornment>
                    }
                    label={getPricingPlanTranslation(zoneMap)}
                    sx={{ backgroundColor: 'background.default' }}
                    aria-describedby="standard-weight-helper-text"
                    onChange={e =>
                      handleSpecialZonePricingPlanPriceChange(
                        zoneMap,
                        e.target.value,
                        currentDomicileClassSpecialZonePricingPlans
                      )
                    }
                    disabled={isSpecialZonePricingPlanDisabled(zoneMap)}
                    value={getSpecialZonePricingPlanPrice(zoneMap, pricingPlans)}
                  />
                </FormControl>
              )
            })}
          </Box>
        </Box>
      )}
      {isTariffEditAllowed(singleTariff) && isOriginalPricingPlan && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            columnGap: 2
          }}
        >
          <Button
            size="medium"
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleOpenActivePeriod}
          >
            {t`Add pricing exception`}
          </Button>
          <Box>
            <Button
              size="small"
              color="inherit"
              endIcon={<MoreVertIcon />}
              aria-label={t`Copy or paste pricing plan`}
              onClick={openCopyPasteMenu}
            >
              {t`Copy/paste`}
            </Button>
            <Menu anchorEl={anchorEl} onClose={closeCopyPasteMenu} open={copyPasteMenuOpen}>
              <MenuItem key="copy" onClick={copyPricingPlan}>
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                {t`Copy domicile class pricing plan`}
              </MenuItem>
              <MenuItem
                key="paste"
                onClick={pastePricingPlan}
                disabled={copiedPricingPlan === undefined}
              >
                <ListItemIcon>
                  <ContentPasteIcon fontSize="small" />
                </ListItemIcon>
                {t`Paste copied domicile class pricing plan`}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ProductPricingGrid
